
// ABOUTME: Custom hook for fetching referrals filtered by role ID
// ABOUTME: Provides referral data and loading states for role-specific referral views

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export function useReferralsByRole(roleId: string) {
  const { user } = useAuth();

  const { data: referrals, isLoading, error } = useQuery({
    queryKey: ['referrals-by-role', roleId],
    queryFn: async () => {
      if (!roleId) return [];

      // First get the referrals
      const { data: referralsData, error: referralsError } = await supabase
        .from('referrals')
        .select(`
          *,
          job_roles (
            title,
            department,
            bonus_amount,
            created_by
          )
        `)
        .eq('job_role_id', roleId)
        .order('submitted_at', { ascending: false });

      if (referralsError) throw referralsError;
      
      if (!referralsData || referralsData.length === 0) return [];

      // Get the job role title for matching
      const jobRole = referralsData[0]?.job_roles?.title;

      // Get scores for these candidates
      const { data: scoresData, error: scoresError } = await supabase
        .from('candidate_score')
        .select('candidate_name, candidate_email, job_role, score');

      if (scoresError) throw scoresError;

      // Match referrals with scores
      const referralsWithScores = referralsData.map(referral => {
        const matchingScore = scoresData?.find(score => 
          score.candidate_name?.toLowerCase() === referral.candidate_name?.toLowerCase() &&
          score.candidate_email?.toLowerCase() === referral.candidate_email?.toLowerCase() &&
          (score.job_role === jobRole || !score.job_role)
        );
        
        return {
          ...referral,
          score: matchingScore?.score || referral.candidate_score || null
        };
      });

      return referralsWithScores;
    },
    enabled: !!roleId && !!user?.id,
  });

  return {
    referrals,
    isLoading,
    error,
  };
}
